import { bearerFetcher, useAuthBearerFetch } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { m2mCleaning_cleanerV3 } from "@/endpoints/m2m-cleaning.cleaner-v3"
import { ApprovalRequest } from "@/model/approvalRequest"
import { m2mCleaning_cleanerV4 } from "@/endpoints/m2m-cleaning.cleaner-v4"
import { DateString } from "@/utils/dateString"
import { CleanerUser } from "@/model/cleaner"

interface UsePendingApprovalRequestsOutput {
    requests: ApprovalRequest[]
}

export const usePendingApprovalRequests = () => {
    const { token } = useAuth()
    const endpoint = m2mCleaning_cleanerV3.listPendingApprovalRequests

    return useAuthBearerFetch<UsePendingApprovalRequestsOutput>(
        token,
        endpoint(),
        {
            method: endpoint.method
        }
    )
}

interface ApprovalRequestForCleanerInput {
    requestIds: string[]
}

type ErrorType = "NotOwnedByCleaner"

interface ErrorResponse {
    errorType: ErrorType
    error: string
}

export const useApprovalRequestMutation = () => {
    const { token } = useAuth()

    const endpoints = {
        approve: m2mCleaning_cleanerV3.approvePendingApprovalRequest,
        reject: m2mCleaning_cleanerV3.rejectPendingApprovalRequest,
        download:
            m2mCleaning_cleanerV4.createSubContractDocumentPdfByApprovalRequestId
    }

    return {
        requestApproveApprovalRequests: (
            input: ApprovalRequestForCleanerInput
        ) =>
            bearerFetcher<unknown, ErrorResponse>(token, endpoints.approve(), {
                method: endpoints.approve.method,
                body: JSON.stringify(input)
            }),
        requestRejectApprovalRequests: (
            input: ApprovalRequestForCleanerInput
        ) =>
            bearerFetcher(token, endpoints.reject(), {
                method: endpoints.reject.method,
                body: JSON.stringify(input)
            }),
        createApprovalRequestsPdf: (approvalRequestId: string) => {
            const url = endpoints.download({ approvalRequestId })
            // eslint-disable-next-line no-restricted-globals -- PDFのBlobレスポンス取得のためfetchを直接使用
            return fetch(url, {
                method: endpoints.download.method,
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
        }
    }
}

type ApprovalRequestByIdOutput = ApprovalRequest

export const useApprovalRequestById = (approvalRequestId: string) => {
    const { token } = useAuth()
    const endpoint = m2mCleaning_cleanerV3.getApprovalRequestByIdAndMySelf

    return useAuthBearerFetch<ApprovalRequestByIdOutput>(
        token,
        endpoint({ approvalRequestId }),
        {
            method: endpoint.method
        }
    )
}

export interface PendingApprovalRequestExistsTodayOutput {
    exists: boolean
}

export const usePendingApprovalRequestExistsToday = () => {
    const { token } = useAuth()

    const endpoint = m2mCleaning_cleanerV3.pendingApprovalRequestExistsToday

    return useAuthBearerFetch<PendingApprovalRequestExistsTodayOutput>(
        token,
        endpoint(),
        {
            method: endpoint.method
        }
    )
}

type ReportedApporvalRequestsByDaysOutput = Record<
    DateString,
    ApprovalRequest[]
>

export const useReportedApprovalRequestsByDays = ({
    targetDates
}: {
    targetDates: DateString[]
}) => {
    const { token } = useAuth()

    const endpoint = m2mCleaning_cleanerV4.getReportedApprovalRequestsByDays

    return useAuthBearerFetch<ReportedApporvalRequestsByDaysOutput>(
        token,
        endpoint(),
        {
            body: JSON.stringify({
                days: targetDates
            }),
            method: endpoint.method
        }
    )
}

export const useCleanerProfile = (token: string) => {
    return useAuthBearerFetch<CleanerUser>(
        token,
        m2mCleaning_cleanerV4.findCleanerProfileForCleaner(),
        {
            method: m2mCleaning_cleanerV4.findCleanerProfileForCleaner.method
        }
    )
}
